<script setup>
import Drawer from "primevue/drawer";
import CloseButton from "@/Components/UI/buttons/CloseButton.vue";
import cn from "@/helpers/cn.js";

defineProps({
    open: {
        type: Boolean,
    },
    width: {
        type: String,
        default: "w-full md:w-sidebar",
    },
});

const emit = defineEmits(["close", "scroll"]);

const close = () => emit("close");
</script>

<template>
    <!--
        Drawer's Portal renders one microtask after mount, so tests must await nextTick
        (see BaseSidebar.test.js) — it is not a production concern.
    -->
    <Drawer
        :visible="open"
        position="right"
        :modal="true"
        :show-close-icon="false"
        :pt="{
            root: { class: cn('border-0 bg-transparent shadow-none p-0 m-0', width) },
        }"
        @update:visible="(v) => !v && close()"
    >
        <!--
            `width` must live on pt.root (the real .p-drawer element), not on the inner div:
            otherwise Drawer keeps Aura's 320px default and wider content overflows the
            viewport, pushing the close × off-screen.
        -->
        <template #container>
            <!--
                @scroll here is a KNOWN no-op: scroll events don't bubble from the inner
                scroller below. Preserved deliberately so NotificationsSidebar's infinite
                scroll behaves exactly as it did pre-refactor; fixing it is a separate task.
            -->
            <div
                class="w-full shadow-dropdown bg-white md:rounded-l-xl flex flex-col min-h-screen max-h-screen"
                data-testid="sidebar-scroll"
                @scroll="(e) => emit('scroll', e)"
            >
                <!-- p-5 matches the content rows' inset; -my-4 keeps the hit target from
                     inflating the row. -mr-4, not the modal's -mr-8, because the rows below
                     scroll and their visible right edge is a scrollbar width further in. -->
                <div class="flex items-center justify-between gap-4 p-5 pt-10 shrink-0 border-b border-dashed border-light-2">
                    <h4 class="font-semibold text-lg min-w-0"><slot name="title" /></h4>
                    <CloseButton
                        class="-my-4 -mr-8 shrink-0 text-2xl"
                        data-testid="sidebar-close"
                        title="Закрыть"
                        @click="close"
                    />
                </div>

                <!--
                    Block flow (space-y), NOT flex-col: inside .p-drawer's flex column a
                    flex-col here gives the slot a definite height, and an in-flow disclosure
                    (SidebarStage, on BaseAccordion) then resolves its header against that and
                    balloons over the rows.
                -->
                <div
                    class="flex-1 overflow-y-scroll space-y-5 divide-y divide-dashed divide-light-2 py-0 px-5"
                >
                    <slot name="content" />
                </div>

                <!-- Footer -->
                <div class="shrink-0">
                    <slot name="footer" />
                </div>
            </div>
        </template>
    </Drawer>
</template>
